<template>
    <div id="clients-page">
        <ClientFormModal ref="modals" @client-created="onClientCreated" />
        <div class="container-fluid">
            <importClients ref="importClients"><span></span></importClients>
            <importOrders ref="importOrders"><span></span></importOrders>
            <importOrders ref="importOrders"><span></span></importOrders>
            <pluginImporter :importer="currentImporter" ref="pluginImporter"><span></span></pluginImporter>
            <loader v-if="is('loading')"></loader>
            <div>
                <div class="d-flex justify-content-between flex-wrap gap-md">
                    <div class="d-flex flex-wrap gap-md align-items-center">
                        <div class="kv-title">{{ stats.totalClients == 0 ? 0 : formatNumber(stats.totalClients, 0) }} {{ t('clients-clients') }}</div>

                        <KvButton class="header-export" prependIcon="ui-download-01" size="large" icon-size="4" variant="outlined" @click="exportClients">
                            {{ t('clients-export') }}
                        </KvButton>

                        <KvButton class="header-new-segment" prepend-icon="ui-plus" size="large" icon-size="4" variant="outlined">
                            <Segments url="clients/create-segment" :filters="filters">
                                {{ t('clients-create_segment') }}
                            </Segments>
                        </KvButton>
                    </div>
                    <!-- <div class="space-separator"></div> -->

                    <div class="d-flex flex-wrap gap-md align-items-center">
                        <KvButton class="header-new-contact" prepend-icon="ui-plus" variant="filled" size="large" icon-size="4" @click="openCreateClient">
                            {{ t('clients-add_contact') }}
                        </KvButton>

                        <KvPopup class="header-import" placement="bottom left">

                            <template #trigger="{ toggle }">
                                <KvButton prepend-icon="ui-upload-02" variant="outlined" size="large" icon-size="4" @click="toggle">
                                    {{ t('clients-import') }}
                                </KvButton>
                            </template>

                            <template #content="{ toggle }">
                                <span class="kv-popup-title">{{ t('clients-import_question') }}</span>

                                <div class="kv-popup-list">
                                    <KvButton alignment="left" prepend-icon="ui-upload-02" variant="outlined" @click="$refs.importOrders.$refs.import.open(); toggle()" :title="t('clients-sales')">
                                        {{ t('clients-sales') }}
                                    </KvButton>
                                    <KvButton alignment="left" prepend-icon="ui-upload-02" variant="outlined" @click="$refs.importClients.$refs.importClients.open(); toggle();" :title="t('clients-clients')">
                                        {{ t('clients-clients') }}
                                    </KvButton>
                                    <KvButton v-for="importer in importers" :key="importer.id" alignment="left" prepend-icon="ui-upload-02" variant="outlined" @click="handlePluginImporterClick(importer); toggle();" :title="importer.name">
                                        {{ importer.name }}
                                    </KvButton>
                                </div>
                            </template>
                        </KvPopup>
                    </div>
                </div>
            </div>
            <div style="margin-top: 15px; margin-bottom: 15px;">
                <KvFilter :defaultSelected="['search']" v-show="ready" ref="filter-bar" @changed="updateFilters" :show-labels="true" :has-period-filter="false" :has-branch-filter="canFilterPerBranch" :filters="extraFilters" />
            </div>
            <div v-show="is('loaded')">
                <grid v-on:sort="setGridSort" display-items-per-page source="clients" :show-search="false" :filters="filters" @loaded="loadedGrid" v-show="stats.totalClients > 0">
                    <template v-slot:rows="grid">
                        <thead>
                            <tr>
                                <grid-th :grid="grid" :sortable="true" attr="name">{{ t('clients-columns-name') }}</grid-th>
                                <grid-th :grid="grid" :sortable="true" attr="whatsapp">{{ t('clients-columns-phone') }}</grid-th>
                                <grid-th :grid="grid" :sortable="true" attr="birthdate" class="col-center">{{ t('clients-columns-birthday') }}</grid-th>

                                <grid-th :grid="grid" :sortable="true" attr="average_value" class="col-center">{{ t('clients-columns-average_ticket') }}</grid-th>
                                <grid-th :grid="grid" :sortable="true" attr="total_value" class="col-center">{{ t('clients-columns-spent') }}</grid-th>
                                <grid-th :grid="grid" :sortable="true" attr="total_orders" class="col-center">{{ t('clients-columns-orders_made') }}</grid-th>
                                <grid-th :grid="grid" :sortable="true" attr="email">{{ t('clients-columns-email') }}</grid-th>
                                <grid-th :grid="grid" :sortable="true" attr="id">{{ t('clients-columns-id') }}</grid-th>
                                <grid-th :grid="grid" :sortable="true" attr="external-id">{{ t('clients-columns-external_id') }}</grid-th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style="cursor: pointer" v-for="client in grid.data" :key="client.clients_id" @click="openClient(client, $event)" tabindex="0" role="button" :aria-label="t('clients-open_contact_aria', [client.first_name, client.last_name])" @keydown="handleRowKey($event, client)">
                                <td style="min-width: 250px" class="font-semibold">
                                    {{ client.first_name }}
                                    {{ client.last_name }}
                                </td>
                                <td style="min-width: 160px">
                                    <div class="d-flex align-items-center">
                                        {{ formatPhone(client.whatsapp) }}
                                        <div class="visible-on-hover">
                                            <btn-whatsapp :number="client.whatsapp"></btn-whatsapp>
                                        </div>
                                    </div>

                                </td>
                                <td style="min-width: 160px" class="col-center">
                                    <template v-if="client.birthdate">
                                        <human-date :date="client.birthdate" :format="t('dates-format_date')" />
                                        ( {{ client.age }} {{ t('clients-years_old') }})
                                    </template>
                                    <span v-else>{{ t('clients-not_informed') }}</span>
                                </td>
                                <td style="min-width: 170px" class="col-center">
                                    {{ currencySymbol }} {{ formatNumber(client.average_value) }}
                                </td>
                                <td style="min-width: 130px" class="col-center">
                                    {{ currencySymbol }} {{ formatNumber(client.total_spend) }}
                                </td>
                                <td style="min-width: 160px" class="col-center">
                                    {{ client.number_orders }}
                                </td>
                                <td style="min-width: 220px">
                                    {{ client.email }}
                                </td>
                                <td>
                                    {{ client.clients_id }}
                                </td>
                                <td>
                                    {{ client.external_id }}
                                </td>

                            </tr>
                        </tbody>
                    </template>
                    <template v-slot:list="grid">
                        <div class="item" v-for="client in grid.data" :key="client.clients_id">
                            <span class="info title">
                                {{ client.first_name }}
                                {{ client.last_name }}
                            </span>
                            <span class="info">
                                <span class="pull-left">{{ formatPhone(client.whatsapp) }}
                                </span>
                                <btn-whatsapp :number="client.whatsapp"></btn-whatsapp>
                            </span>
                            <div class="clear"></div>
                            <span class="info">
                                {{ client.email }}
                            </span>
                            <button class="btn btn-sm btn-primary" @click="
                                $router.push(
                                    'client/' +
                                    client.clients_id,
                                )
                                ">
                                {{ t('clients-details') }}
                            </button>
                        </div>
                    </template>
                </grid>
                <div class="clearfix" v-if="stats.totalClients == 0">
                    <div class="box">
                        <h1>{{ t('clients-empty_state-title') }}</h1>
                        <h2>
                            {{ t('clients-empty_state-subtitle') }}
                        </h2>
                        <div v-if="user.global_admin">
                            <importOrders v-if="user.global_admin" />
                            <br />
                            <importClients v-if="user.global_admin" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped lang="scss">
.dropdown-container {
    position: relative;
}

.dropdown-menu {
    left: auto !important;
    top: 50px !important;
    bottom: auto !important;
    right: 0 !important;
    transform: none !important;
    width: 200px !important;
    padding: 0px !important;
    flex-direction: column;
    gap: 10px;
    border-radius: 5px;
    overflow: hidden;

    >* {
        width: 100%;
        text-align: left;
    }

    ::v-deep {

        .btn,
        button {
            width: 100%;
            text-align: left;
            border-radius: 0;
        }
    }
}
</style>
<script>
import keviApi from "../keviApi.js";
import importOrders from "./import";
import importClients from "./import-clients";
import pluginImporter from "./plugin-importer.vue";
import HumanDate from "../components/human-date/human-date";
import Segments from "../components/segments/segments";
import Papa from "papaparse";
import saveData from "../helpers/saveData";
import Icon from "../components/icon/icon";
import KvFilter from "../components/reports/kv-filter-bar.vue";
import grid from "../components/grid/grid.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import ClientFormModal from "./components/client/client-form-modal.vue";
import phoneMixin from "../mixins/phone-mixin";

export default {
    mixins: [phoneMixin],
    components: {
        HumanDate,
        importOrders,
        importClients,
        pluginImporter,
        Segments,
        KvFilter,
        Icon,
        grid,
        KvButton,
        KvPopup,
        ClientFormModal
    },
    data: function () {
        return {
            state: "loading",
            stats: {
                dataFilledRate: 0,
                totalClients: 0,
                totalWithData: 0,
            },
            exporting: false,
            filters: {},
            sort: {},
            ready: false,
            extraFilters: [],
            importers: [],
            currentImporter: {},
            isActive: false,
        };
    },
    async mounted() {
        document.getElementsByTagName("title")[0].innerText =
            this.t("clients-page_title");

        this.extraFilters.push({
            component: "period",
            label: this.t("clients-filters-bought_in_period"),
            name: "period",
            removable: true,
        });
        this.extraFilters.push({
            component: "period",
            label: this.t("clients-filters-registration_date"),
            name: "createdPeriod",
            removable: true,
        });
        this.extraFilters.push({
            component: "textfield",
            label: this.t("clients-filters-search"),
            name: "search",
            removable: false,
        });
        if (this.$module("segments")) {
            let options = keviApi.getOptions("segments");
            const response = await this.$http(options);
            const segments = response.body;
            if (segments && segments.length > 0) {
                this.extraFilters.push({
                    component: "checkbox",
                    label: this.t("clients-filters-client_segment"),
                    name: "client-segments",
                    options: {
                        options: segments.map((seg) => ({
                            value: seg.id,
                            label: seg.name,
                        })),
                        icon: "ui-users-01",
                    },
                    removable: true,
                });
            }
        }
        this.extraFilters.push({
            component: "period",
            label: this.t("clients-filters-first_order_period"),
            name: "period-first-order",
            removable: true,
        });

        let options = keviApi.getOptions("clients/plugin-importers");
        const importersResponse = await this.$http(options);

        this.importers = importersResponse.body;

        this.ready = true;
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
        user() {
            return this.$store.state.user;
        },
        canFilterPerBranch() {
            return !!this.user.permissions["clients"]["options"][
                "global_access"
            ];
        },
    },
    methods: {
        openCreateClient() {
            this.$refs.modals.openCreateClient();
        },
        onClientCreated(client) {
            if (client && client.id) {
                this.$router.push('/client/' + client.id);
            }
        },
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        setGridSort(sort) {
            this.sort.attr = sort.attr;
            this.sort.sortMethod = sort.method;
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        loadedGrid: function (response) {
            this.stats.totalClients = response.total;
            this.setState("loaded");
        },
        handlePluginImporterClick(importer) {
            this.currentImporter = importer;
            this.$nextTick(() => {
                this.$refs.pluginImporter.openModal();
            });
        },
        openClient(client, e) {
            const path = this.$router.resolve("client/" + client.clients_id);
            if (e.ctrlKey || e.metaKey || e.button === 1) {
                window.open(path.href, "_blank");
            } else {
                this.$router.push(path.location);
            }
        },
        handleRowKey(e, client) {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                this.openClient(client, e);
            }
        },
        updateFilters: function (filters) {
            this.filters = filters;
        },
        exportClients() {
            const options = keviApi.getOptions("clients", "GET", {
                sort: this.sort.attr,
                sortMethod: this.sort.sortMethod,
                filters: this.filters,
                export: true,
            });
            this.exporting = true;
            this.$http(options).then((response) => {
                const clients = response.body.data.map((client) => {
                    let name = client.first_name ? client.first_name : "";
                    if (client.last_name) {
                        name += " " + client.last_name;
                    }

                    var formattedDate = null;
                    if (client.birthdate) {
                        const birthdate = new Date(client.birthdate);
                        formattedDate = birthdate.toLocaleDateString("pt-BR");
                    }

                    return {
                        [this.t("clients-export_headers-name")]: name,
                        [this.t("clients-export_headers-phone")]: this.formatPhone(client.whatsapp),
                        [this.t("clients-export_headers-birthday")]: formattedDate,
                        [this.t("clients-export_headers-age")]: client.age,
                        [this.t("clients-export_headers-email")]: client.email,
                        [this.t("clients-export_headers-average_ticket")]: client.average_value,
                        [this.t("clients-export_headers-spent")]: client.total_spend,
                        [this.t("clients-export_headers-orders_made")]: client.number_orders,
                        [this.t("clients-export_headers-id")]: client.clients_id,
                        [this.t("clients-export_headers-external_id")]: client.external_id,
                    };
                });
                const csv = Papa.unparse(clients);
                saveData(csv, "clients.csv");
                this.exporting = false;
            });
        },
        formatNumber(number, decimal = 2) {
            return number > 0
                ? parseFloat(number).toLocaleString("pt-BR", {
                    maximumFractionDigits: decimal,
                    minimumFractionDigits: decimal,
                })
                : "0,00";
        },
    },
};
</script>
